import moment from "moment";
import { useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { useAssetDetailsContext } from "../../context/assetDetailsContext";
import { fromDate } from "../../reducers/assetDetailsReducer";

export const useSearchParamsToReducer = ({
	searchWorkOrders,
}: {
	searchWorkOrders?: Function;
} = {}) => {
	const [shouldSearch, refetch] = useReducer((x) => x + 1, 0);
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		actions: {
			setAssetsTable,
			setStatisticsTable,
			setWorkOrdersTable,
			setCraftIdCodeWorkOrders,
			setStatusCodeWorkOrders,
			setTypeCodeWorkOrders,
			setSearchTextWorkOrders,
			setLastNWorkOrders,
		},
	} = useAssetDetailsContext();

	useEffect(() => {
		let shouldUpdate = false;

		if (searchParams.has("assets")) {
			const assets = searchParams.get("assets");
			if (assets === "true") {
				setAssetsTable(true);
				shouldUpdate = true;
			} else if (assets === "false") {
				setAssetsTable(false);
				shouldUpdate = true;
			}
		}

		if (searchParams.has("statistics")) {
			const statistics = searchParams.get("statistics");
			if (statistics === "true") {
				setStatisticsTable(true);
				shouldUpdate = true;
			} else if (statistics === "false") {
				setStatisticsTable(false);
				shouldUpdate = true;
			}
		}

		if (searchParams.has("workOrders")) {
			const workOrders = searchParams.get("workOrders");
			if (workOrders === "true") {
				setWorkOrdersTable(true);
				shouldUpdate = true;
			} else if (workOrders === "false") {
				setWorkOrdersTable(false);
				shouldUpdate = true;
			}
		}

		const assets = searchParams.get("assets");
		const statistics = searchParams.get("statistics");
		const workOrders = searchParams.get("workOrders");

		if (
			assets === "false" &&
			statistics === "false" &&
			workOrders === "false"
		) {
			searchParams.set("assets", "true");
			searchParams.set("statistics", "true");
			searchParams.set("workOrders", "true");
			setSearchParams(searchParams, { replace: true });
		}

		if (!searchParams.has("lastN")) {
			searchParams.set("lastN", "20");
			shouldUpdate = true;
		}

		if (!searchParams.has("status")) {
			searchParams.set("status", "O");
			shouldUpdate = true;
		}

		if (!searchParams.has("type")) {
			searchParams.set("type", "A");
			shouldUpdate = true;
		}

		if (!searchParams.has("craft")) {
			searchParams.set("craft", "A");
			shouldUpdate = true;
		}

		let triggerSearch = false;

		if (searchParams.has("lastN")) {
			const lastN = searchParams.get("lastN");
			if (lastN === "") {
				searchParams.delete("lastN");
				shouldUpdate = true;
			} else {
				const lastNNumber = Number.parseInt(lastN!);
				if (!isNaN(lastNNumber) && lastNNumber > 0) {
					setLastNWorkOrders(lastNNumber);
					triggerSearch = true;
				}
			}
		}

		if (searchParams.has("status")) {
			const status = searchParams.get("status");
			if (status !== "A" && status !== "C" && status !== "O") {
				searchParams.delete("status");
				shouldUpdate = true;
			} else {
				setStatusCodeWorkOrders(status);
				triggerSearch = true;
			}
		}

		if (searchParams.has("type")) {
			const type = searchParams.get("type");
			if (type !== "A" && type !== "PM" && type !== "RM") {
				searchParams.delete("type");
				shouldUpdate = true;
			} else {
				setTypeCodeWorkOrders(type);
				triggerSearch = true;
			}
		}

		if (searchParams.has("craft")) {
			const crafts = searchParams.get("craft");
			const craftNumber = Number.parseInt(crafts as string);
			if (crafts !== "A" && (isNaN(craftNumber) || craftNumber < 1)) {
				searchParams.delete("craft");
				shouldUpdate = true;
			} else {
				setCraftIdCodeWorkOrders(Number.parseInt(crafts as string));
				triggerSearch = true;
			}
		}

		if (searchParams.has("search")) {
			const search = searchParams.get("search");
			if (search === "") {
				searchParams.delete("search");
				shouldUpdate = true;
			} else {
				setSearchTextWorkOrders(search);
				triggerSearch = true;
			}
		} else {
			setSearchTextWorkOrders("");
		}

		if (shouldUpdate) {
			setSearchParams(searchParams, { replace: true });
		}

		// Trigger search when parameters change
		if (triggerSearch && searchWorkOrders) {
			refetch();
		}
	}, [searchParams]);

	useEffect(() => {
		if (searchWorkOrders) {
			searchWorkOrders();
		}
	}, [shouldSearch]);
};
